import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import "./styles/App.css";

import Home from "./pages/Home";
import ProfessionalRegister from "./pages/ProfessionalRegister";
import ProfessionalLogin from "./pages/ProfessionalLogin";
import FacilityRegister from "./pages/FacilityRegister";
import FacilityLogin from "./pages/FacilityLogin";
import AdminLogin from "./pages/AdminLogin";

import ProfessionalDashboard from "./pages/ProfessionalDashboard";
import FacilityDashboard from "./pages/FacilityDashboard";
import AdminDashboard from "./pages/AdminDashboard";

import NewsInsights from "./pages/NewsInsights";
import AssignmentDetails from "./pages/AssignmentDetails";
import SavedJobs from "./pages/SavedJobs";

import ProfessionalLicenses from "./pages/professional/ProfessionalLicenses";
import ProfessionalSpecialties from "./pages/professional/ProfessionalSpecialties";
import ProfessionalProfile from "./pages/professional/ProfessionalProfile";
import EditProfessionalProfile from "./pages/professional/EditProfessionalProfile";
import ProfessionalCertifications from "./pages/professional/ProfessionalCertifications";
import ProfessionalDocuments from "./pages/professional/ProfessionalDocuments";
import CredentialManager from "./pages/professional/CredentialManager";
import VerificationStatus from "./pages/professional/VerificationStatus";
import Availability from "./pages/professional/Availability";
import ComingSoon from "./pages/professional/ComingSoon";

import MasterDataManagement from "./pages/admin/MasterDataManagement";
import FacilityManagement from "./pages/admin/FacilityManagement";

import AdminLayout from "./components/layout/AdminLayout";
import FacilityLayout from "./components/layout/FacilityLayout";
import ProfessionalLayout from "./components/layout/ProfessionalLayout";

function CanonicalDomainRedirect() {
  useEffect(() => {
    const hostname =
      window.location.hostname.toLowerCase();

    const isDotCom =
      hostname === "zethhealth.com" ||
      hostname === "www.zethhealth.com";

    if (!isDotCom) {
      return;
    }

    const targetUrl =
      `https://zethhealth.ca` +
      window.location.pathname +
      window.location.search +
      window.location.hash;

    window.location.replace(
      targetUrl
    );
  }, []);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <CanonicalDomainRedirect />
      <ScrollToTop />

      <Routes>
        {/* ========================================
            PUBLIC ROUTES
        ======================================== */}

        <Route
          path="/"
          element={<Home />}
        />

        <Route
          path="/news"
          element={<NewsInsights />}
        />

        <Route
          path="/assignments/:id"
          element={<AssignmentDetails />}
        />

        <Route
          path="/saved-jobs"
          element={<SavedJobs />}
        />

        {/* ========================================
            PROFESSIONAL AUTH
        ======================================== */}

        <Route
          path="/professional/register"
          element={<ProfessionalRegister />}
        />

        <Route
          path="/professional/login"
          element={<ProfessionalLogin />}
        />

        {/* ========================================
            PROFESSIONAL PORTAL
        ======================================== */}

        <Route
          path="/professional"
          element={<ProfessionalLayout />}
        >
          <Route
            index
            element={
              <Navigate
                to="dashboard"
                replace
              />
            }
          />

          <Route
            path="dashboard"
            element={<ProfessionalDashboard />}
          />

          <Route
            path="profile"
            element={<ProfessionalProfile />}
          />

          <Route
            path="profile/edit"
            element={<EditProfessionalProfile />}
          />

          <Route
            path="assignments"
            element={
              <ComingSoon
                title="Assignments"
                description="Browse and manage professional assignments here once the staffing request workflow is connected."
              />
            }
          />

          <Route
            path="saved-jobs"
            element={<SavedJobs />}
          />

          <Route
            path="applications"
            element={
              <ComingSoon
                title="Applications"
                description="Your submitted applications and their status will appear here."
              />
            }
          />

          <Route
            path="documents"
            element={<ProfessionalDocuments />}
          />

          <Route
            path="licenses"
            element={<ProfessionalLicenses />}
          />

          <Route
            path="specialties"
            element={<ProfessionalSpecialties />}
          />

          <Route
            path="certifications"
            element={<ProfessionalCertifications />}
          />

          <Route
            path="availability"
            element={<Availability />}
          />

          <Route
            path="timesheets"
            element={
              <ComingSoon
                title="Timesheets"
                description="Timesheet submission and approval will become available after assignment scheduling is implemented."
              />
            }
          />

          <Route
            path="messages"
            element={
              <ComingSoon
                title="Messages"
                description="Secure communication with Zeth Health and participating facilities will appear here."
              />
            }
          />

          <Route
            path="settings"
            element={
              <ComingSoon
                title="Settings"
                description="Account preferences and professional portal settings will be managed here."
              />
            }
          />

          {/* ========================================
              PROFESSIONAL CREDENTIAL MANAGER
          ======================================== */}

          <Route
            path="credentials"
            element={<CredentialManager />}
          >
            <Route
              index
              element={
                <Navigate
                  to="licenses"
                  replace
                />
              }
            />

            <Route
              path="licenses"
              element={<ProfessionalLicenses />}
            />

            <Route
              path="certifications"
              element={<ProfessionalCertifications />}
            />

            <Route
              path="documents"
              element={<ProfessionalDocuments />}
            />

            <Route
              path="verification"
              element={<VerificationStatus />}
            />
          </Route>
        </Route>

        {/* ========================================
            FACILITY AUTH
        ======================================== */}

        <Route
          path="/facility/register"
          element={<FacilityRegister />}
        />

        <Route
          path="/facility/login"
          element={<FacilityLogin />}
        />

        {/* ========================================
            FACILITY PORTAL
        ======================================== */}

        <Route
          path="/facility"
          element={<FacilityLayout />}
        >
          <Route
            index
            element={
              <Navigate
                to="dashboard"
                replace
              />
            }
          />

          <Route
            path="dashboard"
            element={<FacilityDashboard />}
          />
        </Route>

        {/* ========================================
            ADMIN AUTH
        ======================================== */}

        <Route
          path="/admin/login"
          element={<AdminLogin />}
        />

        {/* ========================================
            ADMIN PORTAL
        ======================================== */}

        <Route
          path="/admin"
          element={<AdminLayout />}
        >
          <Route
            index
            element={
              <Navigate
                to="dashboard"
                replace
              />
            }
          />

          <Route
            path="dashboard"
            element={<AdminDashboard />}
          />

          <Route
            path="facilities"
            element={<FacilityManagement />}
          />

          <Route
            path="master-data"
            element={<MasterDataManagement />}
          />
        </Route>

        {/* ========================================
            FALLBACK
        ======================================== */}

        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;